import React, { useState } from "react";
import { Sparkles, Save, Loader2, Eye, Undo2 } from "lucide-react";
import { updateServicePage, regenerateServicePage } from "../../../api";
import { useToast } from "../../../hooks/use-toast";

const inputCls = "w-full border border-neutral-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-brand";
const Field = ({ label, hint, children }) => (
  <div><label className="block text-xs font-semibold text-neutral-600 mb-1">{label}{hint && <span className="font-normal text-neutral-400"> — {hint}</span>}</label>{children}</div>
);

const linesToList = (s) => s.split("\n").map((x) => x.trim()).filter(Boolean);
const listToLines = (l) => (l || []).join("\n");
const pairsToText = (l, a, b) => (l || []).map((x) => `${x[a]} | ${x[b]}`).join("\n");
const textToPairs = (s, a, b) => linesToList(s).map((line) => { const [x, ...rest] = line.split("|"); return { [a]: x.trim(), [b]: rest.join("|").trim() }; }).filter((x) => x[a]);

const toForm = (p) => ({
  title: p.title || "", tagline: p.tagline || "", hero_image: p.hero_image || "", illustration: p.illustration || "", metier: p.metier || "", tarif: p.tarif || "",
  seo_title: p.seo_title || "", seo_description: p.seo_description || "", published: p.published !== false,
  avantages: listToLines(p.avantages), pour_qui: listToLines(p.pour_qui), inclus: listToLines(p.inclus),
  deroule: pairsToText(p.deroule, "title", "text"), faq: pairsToText(p.faq, "q", "a"),
});
const toPayload = (f, withContent) => {
  const base = { title: f.title, tagline: f.tagline, hero_image: f.hero_image, illustration: f.illustration, tarif: f.tarif, seo_title: f.seo_title, seo_description: f.seo_description, published: f.published };
  if (!withContent) return base;
  return { ...base, metier: f.metier, avantages: linesToList(f.avantages), pour_qui: linesToList(f.pour_qui), inclus: linesToList(f.inclus), deroule: textToPairs(f.deroule, "title", "text"), faq: textToPairs(f.faq, "q", "a") };
};

/** Éditeur d'une page service : champs texte + listes (1 élément par ligne), régénération IA (proposition à valider). */
const ServicePageEditor = ({ page, aiOk, onSaved }) => {
  const [f, setF] = useState(() => toForm(page));
  const [brief, setBrief] = useState("");
  const [busy, setBusy] = useState(null);
  const [backup, setBackup] = useState(null);
  const { toast } = useToast();
  const set = (k) => (e) => setF({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });
  const url = page.external || `/services/${page.slug}`;
  const editable = !page.external || page.slug === "intendance";  // /espaces-verts est une page dédiée (carte, zone 40 km) : seuls titre/accroche/SEO sont repris

  const save = async () => {
    setBusy("save");
    try { const r = await updateServicePage(page.slug, toPayload(f, editable)); toast({ title: "Page enregistrée ✓" }); setBackup(null); onSaved(r.data); }
    catch (e) { toast({ title: "Erreur", description: e?.response?.data?.detail || "Échec", variant: "destructive" }); }
    finally { setBusy(null); }
  };
  const regen = async () => {
    setBusy("ai");
    try {
      const r = await regenerateServicePage(page.slug, brief);
      setBackup(f);
      setF({ ...f, ...toForm({ ...page, ...r.data.proposal }), title: f.title, hero_image: f.hero_image, published: f.published });
      toast({ title: "Proposition IA chargée", description: "Relisez, ajustez puis cliquez sur Enregistrer (ou Annuler la proposition)." });
    } catch (e) { toast({ title: "Génération impossible", description: e?.response?.data?.detail || "Erreur IA", variant: "destructive" }); }
    finally { setBusy(null); }
  };

  return (
    <div className="bg-white border border-neutral-200 rounded-xl p-5 space-y-4" data-testid={`service-editor-${page.slug}`}>
      <div className="flex flex-wrap items-center gap-3 justify-between">
        <div className="flex items-center gap-2 flex-wrap">
          <h3 className="font-heading font-bold text-lg text-neutral-900">{f.title}</h3>
          <a href={url} target="_blank" rel="noreferrer" className="text-xs text-brand hover:underline flex items-center gap-1"><Eye size={12} /> {url}</a>
          {backup && <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">Proposition IA non enregistrée</span>}
        </div>
        <label className="text-sm flex items-center gap-2"><input type="checkbox" className="accent-brand w-4 h-4" checked={f.published} onChange={set("published")} data-testid={`service-published-${page.slug}`} /> Publiée</label>
      </div>

      <div className="flex flex-wrap gap-2 items-end bg-neutral-50 border border-neutral-200 rounded-lg p-3">
        <div className="flex-1 min-w-[240px]">
          <label className="block text-[11px] font-semibold text-neutral-500 mb-1">Consignes pour l'IA (facultatif)</label>
          <input className={inputCls} placeholder="ex : insister sur la réactivité et les sites multi-agences" value={brief} onChange={(e) => setBrief(e.target.value)} data-testid={`service-brief-${page.slug}`} />
        </div>
        <button onClick={regen} disabled={!!busy || !aiOk || !editable} className="flex items-center gap-1.5 bg-neutral-900 hover:bg-neutral-700 text-white text-sm font-semibold px-3 py-2 rounded-md disabled:opacity-50" data-testid={`service-regen-${page.slug}`}>
          {busy === "ai" ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />} Régénérer avec l'IA
        </button>
        {backup && <button onClick={() => { setF(backup); setBackup(null); }} className="flex items-center gap-1.5 text-sm text-neutral-600 border border-neutral-300 px-3 py-2 rounded-md" data-testid={`service-undo-${page.slug}`}><Undo2 size={14} /> Annuler la proposition</button>}
      </div>

      <div className="grid md:grid-cols-2 gap-3">
        <Field label="Titre"><input className={inputCls} value={f.title} onChange={set("title")} data-testid={`service-title-${page.slug}`} /></Field>
        <Field label="Accroche"><input className={inputCls} value={f.tagline} onChange={set("tagline")} data-testid={`service-tagline-${page.slug}`} /></Field>
        <Field label="Image d'en-tête (URL)"><input className={inputCls} value={f.hero_image} onChange={set("hero_image")} /></Field>
        <Field label="Tarification"><input className={inputCls} value={f.tarif} onChange={set("tarif")} /></Field>
        {page.slug === "intendance" && <Field label="Illustration (URL)"><input className={inputCls} value={f.illustration} onChange={set("illustration")} /></Field>}
      </div>
      {!editable && <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2" data-testid={`service-static-${page.slug}`}>Cette page ({url}) a une mise en page dédiée (carte, zone 40 km) : son contenu détaillé est fixé dans le code. Seuls le titre, l'accroche (menu / accueil), le tarif et le SEO sont modifiables ici.</p>}
      {editable && (<>
        <Field label="Le métier" hint="paragraphes séparés par une ligne vide"><textarea rows={5} className={inputCls} value={f.metier} onChange={set("metier")} /></Field>
        <div className="grid md:grid-cols-3 gap-3">
          <Field label="Avantages" hint="1 par ligne"><textarea rows={6} className={inputCls} value={f.avantages} onChange={set("avantages")} /></Field>
          <Field label="Pour qui" hint="1 par ligne"><textarea rows={6} className={inputCls} value={f.pour_qui} onChange={set("pour_qui")} /></Field>
          <Field label="Inclus" hint="1 par ligne"><textarea rows={6} className={inputCls} value={f.inclus} onChange={set("inclus")} /></Field>
        </div>
        <div className="grid md:grid-cols-2 gap-3">
          <Field label="Déroulé" hint="Titre | texte, 1 étape par ligne"><textarea rows={5} className={inputCls} value={f.deroule} onChange={set("deroule")} /></Field>
          <Field label="FAQ" hint="Question | réponse, 1 par ligne"><textarea rows={5} className={inputCls} value={f.faq} onChange={set("faq")} /></Field>
        </div>
      </>)}
      <div className="grid md:grid-cols-2 gap-3">
        <Field label="Titre SEO" hint={`${f.seo_title.length}/65`}><input className={inputCls} value={f.seo_title} onChange={set("seo_title")} /></Field>
        <Field label="Description SEO" hint={`${f.seo_description.length}/160`}><input className={inputCls} value={f.seo_description} onChange={set("seo_description")} /></Field>
      </div>
      <div className="flex justify-end">
        <button onClick={save} disabled={!!busy} className="flex items-center gap-1.5 bg-brand hover:bg-brand-dark text-white text-sm font-semibold px-4 py-2 rounded-md disabled:opacity-60" data-testid={`service-save-${page.slug}`}>
          {busy === "save" ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Enregistrer
        </button>
      </div>
    </div>
  );
};

export default ServicePageEditor;
